import { useMemo } from 'react';
import { isAppModule } from '@utils/constants';
import { metricsStore } from '@globalStore/metricsStore';
import { isPercentileMetric } from '@src/utils/percentile';

function useMetricCategories({
  supportedMetrics,
  selectedMetricList = [],
  lockedMetricsSet,
  excludeCategories = [],
  allMetrics,
  shrinkPercentileMetric = true,
}: any) {
  const { metricsCategoryKeys } = metricsStore.getState();
  const isAppContext = isAppModule;

  const metricCategories = useMemo(() => {
    const categoryMap: any = {};
    const currentMetricCategory = metricsCategoryKeys ?? {};
    Object.values(currentMetricCategory).map((category: any) => (categoryMap[category] = []));

    for (const m in supportedMetrics) {
      const metric = allMetrics[m];
      if (!metric || !metric.active) {
        continue;
      }

      /** As custom metrics are imported, check whether metric exist before use it to avoid error and page crash */
      if (
        !metric ||
        !('category' in metric) ||
        (shrinkPercentileMetric && isPercentileMetric(metric)) ||
        metric.flowID
      ) {
        continue;
      }

      let disabled = lockedMetricsSet.has(metric.name);
      if (isAppContext) {
        disabled =
          disabled ||
          metric.definitionStatus === 'undefined' ||
          metric.definitionStatus === 'in_progress';
      }
      if (metric['percentileMetrics']) {
        metric['percentileMetrics'].forEach((it) => {
          it.checked = selectedMetricList.includes(it.name);
          it.disabled = lockedMetricsSet.has(it.name);
        });
      }
      const checked = selectedMetricList.includes(metric.name);

      // a metric can be part of multiple categories
      const categories: any = Array.isArray(metric.category) ? metric.category : [metric.category];
      categories.forEach((category) => {
        categoryMap[category]?.push({
          key: metric.name,
          unit: metric.unit,
          name: `${metric.displayName} ${metric.subscript}`,
          checked,
          disabled,
          isHeavyMetric: metric.isHeavyMetric,
          definitionStatus: metric.definitionStatus,
          percentileMetrics: metric.percentileMetrics,
          isDeployed: metric.isDeployed,
        });
      });
    }

    // delete excluded categories
    excludeCategories.forEach((category) => {
      delete categoryMap[category];
    });

    // Sorting the options alphabetically for each category
    for (const category in categoryMap) {
      categoryMap[category].sort((a, b) => a.name.localeCompare(b.name));
    }

    return categoryMap;
  }, [supportedMetrics, selectedMetricList, lockedMetricsSet, excludeCategories, allMetrics]);

  return metricCategories;
}

export default useMetricCategories;
